import { createPortal } from "react-dom"
import { useState } from "react"
import { Check, Copy, ShieldCheck, X } from "lucide-react"

/**
 * Zeigt ein frisch erzeugtes 2FA-Geheimnis — genau einmal.
 *
 * Ersetzt „2FA lösen" (25.08.2026). Bis dahin gab es nur den Weg, jemandem den
 * zweiten Faktor wegzunehmen: am 25.08. verlor Robin ihn so, der Zugang lief
 * ungeschützt weiter, und nichts im Portal erinnerte daran. Erneuern löst
 * dasselbe Problem — Handy weg, App neu aufgesetzt — und lässt den Schutz stehen.
 *
 * 🔴 Das Geheimnis steht nur in dieser einen Antwort. Es wird nicht
 * protokolliert und lässt sich über keinen Endpunkt nachlesen. Deshalb hat der
 * Dialog nur einen Knopf zum Schließen und sagt vorher, was das bedeutet.
 *
 * 🔑 Kein QR-Code: dafür bräuchte es eine weitere Abhängigkeit im Frontend, und
 * der Zweck ist hier ohnehin die Weitergabe an eine andere Person — die tippt
 * den Schlüssel in ihre App („Schlüssel manuell eingeben"). Für sich selbst
 * öffnet der Link die App direkt.
 */

export type ZweiFaktorGeheimnis = {
  username?: string
  secret: string
  secret_lesbar: string
  uri: string
  hinweis?: string
}

export function ZweiFaktorDialog({ daten, onClose }: {
  daten: ZweiFaktorGeheimnis
  onClose: () => void
}) {
  const [kopiert, setKopiert] = useState(false)

  const kopieren = async () => {
    try {
      await navigator.clipboard.writeText(daten.secret)
      setKopiert(true)
      setTimeout(() => setKopiert(false), 2000)
    } catch {
      /* Ohne Zwischenablage bleibt der lesbare Schlüssel zum Abtippen. */
    }
  }

  // 🔑 createPortal: sonst schneidet ein `overflow: hidden` weiter oben im
  // Baum den Dialog ab — dieselbe Falle wie bei CreateContactModal.
  return createPortal(
    <div
      className="pp-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Neuer zweiter Faktor"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(15,23,42,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
    >
      <div style={{
        width: "100%", maxWidth: 460, borderRadius: 10, padding: 20,
        background: "var(--bg-card)", border: "1px solid var(--border-color)",
        boxShadow: "0 18px 40px rgba(0,0,0,0.28)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <ShieldCheck size={17} />
          <strong style={{ fontSize: 15 }}>
            Neuer zweiter Faktor{daten.username ? ` für „${daten.username}“` : ""}
          </strong>
          <button type="button" onClick={onClose} aria-label="Schließen"
                  style={{ marginLeft: "auto", background: "none", border: "none",
                           cursor: "pointer", color: "var(--text-muted)" }}>
            <X size={16} />
          </button>
        </div>

        <p style={{ fontSize: 12.5, color: "var(--text-muted)", marginBottom: 12 }}>
          {daten.hinweis || "Einmalige Anzeige — danach ist der Schlüssel nicht mehr abrufbar."}
          {" "}Der alte Code gilt ab sofort nicht mehr.
        </p>

        <div style={{
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: 15, letterSpacing: 1, wordBreak: "break-all",
          padding: "10px 12px", borderRadius: 7, marginBottom: 8,
          border: "1px solid var(--border-color)", background: "var(--bg)",
        }}>
          {daten.secret_lesbar}
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
          <button type="button" onClick={kopieren}
                  style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12.5,
                           padding: "6px 10px", borderRadius: 6, cursor: "pointer",
                           border: "1px solid var(--border-color)", background: "var(--bg-card)",
                           color: "var(--text)" }}>
            {kopiert ? <><Check size={13} /> Kopiert</> : <><Copy size={13} /> Schlüssel kopieren</>}
          </button>
          <a href={daten.uri}
             style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12.5,
                      padding: "6px 10px", borderRadius: 6, textDecoration: "none",
                      border: "1px solid var(--border-color)", color: "var(--text)" }}>
            In meiner Authenticator-App öffnen
          </a>
        </div>

        <p style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 14 }}>
          In der App „Schlüssel manuell eingeben“ wählen, den Schlüssel eintragen,
          Zeitbasiert (TOTP), 6 Stellen, 30 Sekunden.
          {daten.username && (
            <> Auf einem Weg weitergeben, auf dem er nicht stehen bleibt — nicht per
            E-Mail oder Chat-Verlauf. Wer ihn bekommt, kann ihn unter „Mein Konto“
            selbst erneuern; dann kennt ihn wieder nur einer.</>
          )}
        </p>

        <button type="button" onClick={onClose}
                style={{ fontSize: 13, fontWeight: 500, padding: "7px 14px", borderRadius: 6,
                         border: "none", background: "#3C3489", color: "#fff", cursor: "pointer" }}>
          Fertig — nicht mehr anzeigen
        </button>
      </div>
    </div>,
    document.body,
  )
}
